import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Slider } from '../../components/ui/slider';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Key, Copy, RefreshCw, Sparkles, Shield, Lock, 
  Zap, CheckCircle2, AlertTriangle, Wand2, 
  Eye, EyeOff, Download, Trash2, Info, ShieldCheck
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

// Character sets - defined outside component to avoid recreating on each render
const charSets = {
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  numbers: '0123456789',
  symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?'
};

// Word list for passphrase generation - defined outside component
const wordList = [
  'correct', 'horse', 'battery', 'staple', 'mountain', 'river', 'ocean', 'forest',
  'thunder', 'lightning', 'rainbow', 'sunset', 'sunrise', 'galaxy', 'planet', 'comet',
  'phoenix', 'dragon', 'wizard', 'castle', 'knight', 'warrior', 'cipher', 'quantum',
  'crystal', 'shadow', 'spark', 'flame', 'frost', 'storm', 'cloud', 'solar',
  'lunar', 'stellar', 'cosmic', 'nebula', 'asteroid', 'meteor', 'eclipse', 'aurora',
  'mystic', 'enigma', 'puzzle', 'riddle', 'secret', 'hidden', 'silent', 'whisper',
  'echo', 'dream', 'vision', 'spirit', 'energy', 'power', 'force', 'strength',
  'valor', 'honor', 'glory', 'triumph', 'victory', 'legend', 'myth', 'saga'
];

export const PasswordGeneratorPage = () => {
  // Random Generator State
  const [password, setPassword] = useState('');
  const [length, setLength] = useState([16]);
  const [useUppercase, setUseUppercase] = useState(true);
  const [useLowercase, setUseLowercase] = useState(true);
  const [useNumbers, setUseNumbers] = useState(true);
  const [useSymbols, setUseSymbols] = useState(true);
  const [showPassword, setShowPassword] = useState(true);
  
  // Smart Name Generator State
  const [nameInput, setNameInput] = useState('');
  const [smartPassword, setSmartPassword] = useState('');
  const [strengthLevel, setStrengthLevel] = useState('balanced');
  
  // Passphrase Generator State
  const [passphrase, setPassphrase] = useState('');
  const [wordCount, setWordCount] = useState([4]);
  const [separator, setSeparator] = useState('-');
  
  // History State (session only)
  const [passwordHistory, setPasswordHistory] = useState([]);
  const [autoClearTimeout, setAutoClearTimeout] = useState(null);

  // Add to history (session only)
  const addToHistory = useCallback((pwd, type) => {
    const newEntry = {
      password: pwd,
      type,
      timestamp: new Date().toLocaleTimeString(),
      id: Date.now()
    };
    setPasswordHistory(prev => [newEntry, ...prev].slice(0, 10));
  }, []);

  // Generate random password
  const generateRandomPassword = useCallback(() => {
    let charset = '';
    if (useUppercase) charset += charSets.uppercase;
    if (useLowercase) charset += charSets.lowercase;
    if (useNumbers) charset += charSets.numbers;
    if (useSymbols) charset += charSets.symbols;

    if (charset === '') {
      toast.error('Please select at least one character type');
      return;
    }

    let newPassword = '';
    const array = new Uint32Array(length[0]);
    window.crypto.getRandomValues(array);
    
    for (let i = 0; i < length[0]; i++) {
      newPassword += charset[array[i] % charset.length];
    }

    setPassword(newPassword);
    addToHistory(newPassword, 'Random');
    toast.success('Password generated!', {
      description: `Length: ${length[0]} characters`
    });
  }, [useUppercase, useLowercase, useNumbers, useSymbols, length, addToHistory]);

  // Generate smart password from name
  const generateSmartPassword = useCallback(() => {
    if (!nameInput.trim()) {
      toast.error('Please enter text to convert');
      return;
    }

    let result = nameInput.trim();
    
    // Character substitutions based on strength level
    const substitutions = {
      readable: {
        'a': '@', 'e': '3', 'i': '!', 'o': '0', 's': '$'
      },
      balanced: {
        'a': '@', 'A': '4', 'e': '3', 'E': '€', 'i': '!', 'I': '1',
        'o': '0', 'O': '0', 's': '$', 'S': '5', 't': '7', 'l': '1'
      },
      maximum: {
        'a': '@', 'A': '4', 'b': '8', 'B': '8', 'e': '3', 'E': '€',
        'g': '9', 'i': '!', 'I': '1', 'l': '1', 'o': '0', 'O': '0',
        's': '$', 'S': '5', 't': '7', 'T': '7', 'z': '2', 'Z': '2'
      }
    };

    const subs = substitutions[strengthLevel];
    
    // Apply substitutions
    result = result.split('').map(char => subs[char] || char).join('');

    // Add complexity based on strength level
    if (strengthLevel === 'readable') {
      // Add simple suffix
      const randomNum = Math.floor(Math.random() * 100);
      result = result + randomNum;
    } else if (strengthLevel === 'balanced') {
      // Mix case and add numbers/symbols
      if (result === result.toLowerCase()) {
        result = result.charAt(0).toUpperCase() + result.slice(1);
      }
      const symbols = '!@#$%';
      const randomSymbol = symbols[Math.floor(Math.random() * symbols.length)];
      const randomNum = Math.floor(Math.random() * 100);
      result = result + randomSymbol + randomNum;
    } else {
      // Maximum security
      const symbols = '!@#$%^&*';
      const randomSymbol1 = symbols[Math.floor(Math.random() * symbols.length)];
      const randomSymbol2 = symbols[Math.floor(Math.random() * symbols.length)];
      const randomNum = Math.floor(Math.random() * 1000);
      result = randomSymbol1 + result + randomSymbol2 + randomNum;
    }

    setSmartPassword(result);
    addToHistory(result, 'Smart');
    toast.success('Smart password created!', {
      description: `From: ${nameInput}`
    });
  }, [nameInput, strengthLevel, addToHistory]);

  // Generate memorable passphrase
  const generatePassphrase = useCallback(() => {
    const selectedWords = [];
    const array = new Uint32Array(wordCount[0]);
    window.crypto.getRandomValues(array);
    
    for (let i = 0; i < wordCount[0]; i++) {
      const randomIndex = array[i] % wordList.length;
      selectedWords.push(wordList[randomIndex]);
    }

    let result = selectedWords.join(separator);
    
    // Capitalize first letter of each word if using hyphen or underscore
    if (separator === '-' || separator === '_') {
      result = selectedWords
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(separator);
    }

    setPassphrase(result);
    addToHistory(result, 'Passphrase');
    toast.success('Passphrase generated!', {
      description: `${wordCount[0]} words`
    });
  }, [wordCount, separator, addToHistory]);

  // Copy to clipboard
  const copyToClipboard = useCallback(async (text, shouldAutoClear = false) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!', {
        description: shouldAutoClear ? 'Will auto-clear in 30 seconds' : undefined
      });
      
      if (shouldAutoClear && autoClearTimeout) {
        clearTimeout(autoClearTimeout);
      }
      
      if (shouldAutoClear) {
        const timeout = setTimeout(() => {
          navigator.clipboard.writeText('');
          toast.info('Clipboard cleared for security');
        }, 30000);
        setAutoClearTimeout(timeout);
      }
    } catch (err) {
      toast.error('Failed to copy');
    }
  }, [autoClearTimeout]);

  // Calculate entropy
  const calculateEntropy = useCallback((pwd) => {
    if (!pwd) return 0;
    let charsetSize = 0;
    if (/[a-z]/.test(pwd)) charsetSize += 26;
    if (/[A-Z]/.test(pwd)) charsetSize += 26;
    if (/[0-9]/.test(pwd)) charsetSize += 10;
    if (/[^a-zA-Z0-9]/.test(pwd)) charsetSize += 32;
    
    return Math.log2(Math.pow(charsetSize, pwd.length));
  }, []);

  // Get entropy level
  const getEntropyLevel = useCallback((entropy) => {
    if (entropy < 50) return { label: 'Weak', color: 'bg-red-500', icon: AlertTriangle };
    if (entropy < 75) return { label: 'Fair', color: 'bg-yellow-500', icon: Shield };
    if (entropy < 100) return { label: 'Good', color: 'bg-blue-500', icon: ShieldCheck };
    return { label: 'Excellent', color: 'bg-green-500 cyber-glow', icon: CheckCircle2 };
  }, []);

  // Export passwords
  const exportPasswords = useCallback(() => {
    if (passwordHistory.length === 0) {
      toast.error('No passwords to export');
      return;
    }

    const exportData = passwordHistory.map(entry => 
      `${entry.timestamp} - ${entry.type}: ${entry.password}`
    ).join('\n');

    const blob = new Blob([exportData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `passwords-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Passwords exported!', {
      description: 'Remember to store securely'
    });
  }, [passwordHistory]);

  // Clear history
  const clearHistory = useCallback(() => {
    setPasswordHistory([]);
    toast.success('History cleared');
  }, []);

  // Auto-generate on mount
  useEffect(() => {
    generateRandomPassword();
    generatePassphrase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Password display component
  const PasswordDisplay = ({ pwd, label, onRegenerate, type = 'random' }) => {
    const entropy = calculateEntropy(pwd);
    const entropyInfo = getEntropyLevel(entropy);
    const EntropyIcon = entropyInfo.icon;

    return (
      <div className="space-y-4">
        <div className="relative">
          <div className="bg-gradient-to-r from-card to-secondary/50 border-2 border-border rounded-xl p-6 min-h-[120px] flex flex-col justify-center">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
              {label}
            </Label>
            <div className="flex items-center gap-3">
              <div className="flex-1 font-mono text-2xl break-all leading-relaxed">
                {showPassword ? (
                  <span className="text-foreground">{pwd || 'Generate a password...'}</span>
                ) : (
                  <span className="text-primary">{'•'.repeat(pwd?.length || 16)}</span>
                )}
              </div>
            </div>
            
            {pwd && (
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <Badge variant="outline" className="text-xs">
                  {pwd.length} chars
                </Badge>
                <Badge className={`${entropyInfo.color} text-white border-0`}>
                  <EntropyIcon className="w-3 h-3 mr-1" />
                  {entropyInfo.label}
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  {entropy.toFixed(0)} bits entropy
                </Badge>
              </div>
            )}
          </div>
        </div>

        {pwd && (
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => copyToClipboard(pwd, true)}
              className="bg-primary hover:bg-primary/90 cyber-glow"
              data-testid={`copy-${type}-password`}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </Button>
            <Button
              onClick={onRegenerate}
              variant="outline"
              className="border-primary/50 hover:bg-primary/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Regenerate
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-card dark">
      <div className="container mx-auto px-4 py-12 space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 border-2 border-primary/50 cyber-glow mb-4">
            <Key className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent cyber-text-glow">
            Password Generator
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Generate ultra-secure passwords with military-grade randomness. All processing happens locally in your browser.
          </p>
          
          {/* Privacy Badge */}
          <div className="flex items-center justify-center gap-2 text-sm text-primary">
            <ShieldCheck className="w-4 h-4" />
            <span className="font-medium">100% Local Processing • Zero Storage • Complete Privacy</span>
          </div>
        </div>

        {/* Show/Hide Password Toggle */}
        <div className="flex justify-center">
          <Button
            onClick={() => setShowPassword(!showPassword)}
            variant="outline"
            size="sm"
            className="border-primary/30"
          >
            {showPassword ? (
              <>
                <EyeOff className="w-4 h-4 mr-2" />
                Hide Passwords
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-2" />
                Show Passwords
              </>
            )}
          </Button>
        </div>

        <Tabs defaultValue="random" className="w-full">
          <TabsList className="grid w-full grid-cols-4 lg:w-[900px] mx-auto mb-8 bg-card/50 border border-border">
            <TabsTrigger 
              value="random" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="tab-random"
            >
              <Zap className="w-4 h-4 mr-2" />
              Random
            </TabsTrigger>
            <TabsTrigger 
              value="smart" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="tab-smart"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              Smart
            </TabsTrigger>
            <TabsTrigger 
              value="passphrase" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="tab-passphrase"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Passphrase
            </TabsTrigger>
            <TabsTrigger 
              value="privacy" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="tab-privacy"
            >
              <Lock className="w-4 h-4 mr-2" />
              Privacy
            </TabsTrigger>
          </TabsList>

          {/* Random Generator Tab */}
          <TabsContent value="random" className="space-y-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Configuration */}
              <Card className="border-border bg-card/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <Zap className="w-6 h-6 text-primary" />
                    Configuration
                  </CardTitle>
                  <CardDescription>Customize your password requirements</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <Label className="text-base font-semibold">Length: {length[0]}</Label>
                    <Slider
                      value={length}
                      onValueChange={setLength}
                      min={6}
                      max={64}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Weak</span>
                      <span>Strong</span>
                      <span>Ultra Secure</span>
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 border-t border-border">
                    <Label className="text-base font-semibold">Character Types</Label>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                        <div className="space-y-1">
                          <Label htmlFor="uppercase" className="cursor-pointer font-medium">
                            Uppercase Letters
                          </Label>
                          <p className="text-xs text-muted-foreground">A-Z</p>
                        </div>
                        <Switch
                          id="uppercase"
                          checked={useUppercase}
                          onCheckedChange={setUseUppercase}
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                        <div className="space-y-1">
                          <Label htmlFor="lowercase" className="cursor-pointer font-medium">
                            Lowercase Letters
                          </Label>
                          <p className="text-xs text-muted-foreground">a-z</p>
                        </div>
                        <Switch
                          id="lowercase"
                          checked={useLowercase}
                          onCheckedChange={setUseLowercase}
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                        <div className="space-y-1">
                          <Label htmlFor="numbers" className="cursor-pointer font-medium">
                            Numbers
                          </Label>
                          <p className="text-xs text-muted-foreground">0-9</p>
                        </div>
                        <Switch
                          id="numbers"
                          checked={useNumbers}
                          onCheckedChange={setUseNumbers}
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                        <div className="space-y-1">
                          <Label htmlFor="symbols" className="cursor-pointer font-medium">
                            Symbols
                          </Label>
                          <p className="text-xs text-muted-foreground">!@#$%^&*()</p>
                        </div>
                        <Switch
                          id="symbols"
                          checked={useSymbols}
                          onCheckedChange={setUseSymbols}
                        />
                      </div>
                    </div>
                  </div>

                  <Alert className="bg-primary/10 border-primary/30">
                    <Info className="w-4 h-4 text-primary" />
                    <AlertDescription className="text-sm">
                      <strong>Recommended:</strong> Enable all character types and use at least 16 characters for maximum security.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

              {/* Preview */}
              <Card className="border-border bg-card/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <Lock className="w-6 h-6 text-primary" />
                    Generated Password
                  </CardTitle>
                  <CardDescription>Your secure random password</CardDescription>
                </CardHeader>
                <CardContent>
                  <PasswordDisplay
                    pwd={password}
                    label="Random Password"
                    onRegenerate={generateRandomPassword}
                    type="random"
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Smart Generator Tab */}
          <TabsContent value="smart" className="space-y-8">
            <div className="grid lg:grid-cols-2 gap-8">
              <Card className="border-border bg-card/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <Wand2 className="w-6 h-6 text-primary" />
                    Smart Name Converter
                  </CardTitle>
                  <CardDescription>Transform memorable text into secure passwords</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <Label htmlFor="name-input" className="text-base font-semibold">
                      Enter Name or Phrase
                    </Label>
                    <Input
                      id="name-input"
                      placeholder="e.g., Praneeth, MyCompany, etc."
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      className="h-12 text-lg"
                      data-testid="smart-name-input"
                    />
                  </div>

                  <div className="space-y-4">
                    <Label className="text-base font-semibold">Strength Level</Label>
                    <Select value={strengthLevel} onValueChange={setStrengthLevel}>
                      <SelectTrigger className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="readable">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">Readable</Badge>
                            <span>Easy to remember</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="balanced">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-blue-500">Balanced</Badge>
                            <span>Good security & usability</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="maximum">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-primary">Maximum</Badge>
                            <span>Highest security</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={generateSmartPassword}
                    className="w-full h-12 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 cyber-glow"
                    disabled={!nameInput.trim()}
                    data-testid="generate-smart-btn"
                  >
                    <Wand2 className="w-5 h-5 mr-2" />
                    Generate Smart Password
                  </Button>

                  <Alert className="bg-accent/10 border-accent/30">
                    <Sparkles className="w-4 h-4 text-accent" />
                    <AlertDescription className="text-sm">
                      <strong>How it works:</strong> Your text is intelligently transformed with character substitutions (a→@, e→3) and complexity additions based on your chosen strength level.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

              <Card className="border-border bg-card/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <Lock className="w-6 h-6 text-primary" />
                    Smart Password
                  </CardTitle>
                  <CardDescription>Transformed secure password</CardDescription>
                </CardHeader>
                <CardContent>
                  <PasswordDisplay
                    pwd={smartPassword}
                    label="Smart Password"
                    onRegenerate={generateSmartPassword}
                    type="smart"
                  />
                  
                  {nameInput && smartPassword && (
                    <div className="mt-6 p-4 bg-accent/10 rounded-lg border border-accent/30">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Transformation Example
                      </Label>
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground text-sm">Original:</span>
                          <code className="text-foreground font-mono">{nameInput}</code>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-primary text-sm font-semibold">Transformed:</span>
                          <code className="text-primary font-mono font-semibold">{smartPassword}</code>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Passphrase Tab */}
          <TabsContent value="passphrase" className="space-y-8">
            <div className="grid lg:grid-cols-2 gap-8">
              <Card className="border-border bg-card/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <Sparkles className="w-6 h-6 text-primary" />
                    Passphrase Settings
                  </CardTitle>
                  <CardDescription>Create memorable yet secure passphrases</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <Label className="text-base font-semibold">
                      Number of Words: {wordCount[0]}
                    </Label>
                    <Slider
                      value={wordCount}
                      onValueChange={setWordCount}
                      min={3}
                      max={8}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Short</span>
                      <span>Balanced</span>
                      <span>Long</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <Label className="text-base font-semibold">Word Separator</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { value: '-', label: 'Hyphen' },
                        { value: '_', label: 'Underscore' },
                        { value: '.', label: 'Period' },
                        { value: '', label: 'None' }
                      ].map((sep) => (
                        <Button
                          key={sep.value}
                          onClick={() => setSeparator(sep.value)}
                          variant={separator === sep.value ? 'default' : 'outline'}
                          className={separator === sep.value ? 'bg-primary cyber-glow' : ''}
                        >
                          {sep.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={generatePassphrase}
                    className="w-full h-12 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 cyber-glow"
                    data-testid="generate-passphrase-btn"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Passphrase
                  </Button>

                  <Alert className="bg-primary/10 border-primary/30">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <AlertDescription className="text-sm">
                      <strong>Why passphrases?</strong> They're easier to remember than random characters while still providing excellent security through length and word combinations.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

              <Card className="border-border bg-card/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <Lock className="w-6 h-6 text-primary" />
                    Generated Passphrase
                  </CardTitle>
                  <CardDescription>Your memorable passphrase</CardDescription>
                </CardHeader>
                <CardContent>
                  <PasswordDisplay
                    pwd={passphrase}
                    label="Passphrase"
                    onRegenerate={generatePassphrase}
                    type="passphrase"
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Privacy Tab */}
          <TabsContent value="privacy">
            <Card className="border-border bg-card/50 backdrop-blur-sm max-w-5xl mx-auto">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-3xl">
                  <ShieldCheck className="w-8 h-8 text-primary" />
                  Privacy & Security Guarantee
                </CardTitle>
                <CardDescription className="text-base">
                  Complete transparency about how your passwords are generated and handled
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="p-6 rounded-xl bg-primary/10 border-2 border-primary/30">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                        <Lock className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold">100% Local Processing</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      All password generation happens entirely in your browser using the Web Crypto API. 
                      Your passwords never touch any server, database, or third-party service.
                    </p>
                  </div>

                  <div className="p-6 rounded-xl bg-accent/10 border-2 border-accent/30">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                        <ShieldCheck className="w-6 h-6 text-accent" />
                      </div>
                      <h3 className="text-xl font-bold">Zero Storage</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      No passwords are saved to localStorage, sessionStorage, cookies, or any persistent storage. 
                      When you close the tab, everything is gone forever.
                    </p>
                  </div>

                  <div className="p-6 rounded-xl bg-blue-500/10 border-2 border-blue-500/30">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <Zap className="w-6 h-6 text-blue-500" />
                      </div>
                      <h3 className="text-xl font-bold">Cryptographically Secure</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      Uses <code className="text-primary">window.crypto.getRandomValues()</code> for 
                      true cryptographic randomness, not predictable pseudo-random algorithms.
                    </p>
                  </div>

                  <div className="p-6 rounded-xl bg-yellow-500/10 border-2 border-yellow-500/30">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                        <Info className="w-6 h-6 text-yellow-500" />
                      </div>
                      <h3 className="text-xl font-bold">No Tracking</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      Zero analytics, no fingerprinting, no telemetry. We don't know what passwords 
                      you generate, when you generate them, or how you use them.
                    </p>
                  </div>
                </div>

                <Alert className="bg-primary/10 border-primary/30">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  <AlertDescription className="text-base">
                    <strong className="text-primary">Security Recommendation:</strong> Always use unique passwords for each account. 
                    Consider using a password manager to securely store your generated passwords.
                  </AlertDescription>
                </Alert>

                <div className="space-y-4 p-6 bg-muted/50 rounded-xl border border-border">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Technical Details
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span><strong>Entropy Calculation:</strong> Passwords are scored based on character set diversity and length</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span><strong>Smart Generation:</strong> Intelligent character substitutions maintain memorability while adding complexity</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span><strong>Passphrase Library:</strong> Curated word list of 64 memorable, distinct words</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span><strong>Session History:</strong> In-memory only, limited to 10 entries, cleared on page refresh</span>
                    </li>
                  </ul>
                </div>

                <div className="text-center p-8 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border-2 border-primary/30">
                  <ShieldCheck className="w-16 h-16 text-primary mx-auto mb-4 cyber-glow" />
                  <h3 className="text-2xl font-bold mb-2">Your Privacy is Non-Negotiable</h3>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    This tool is built with privacy-first principles. All code runs client-side, 
                    works offline after initial load, and leaves no trace of your passwords anywhere.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Password History */}
        {passwordHistory.length > 0 && (
          <Card className="border-border bg-card/50 backdrop-blur-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <RefreshCw className="w-6 h-6 text-primary" />
                    Recent Passwords
                  </CardTitle>
                  <CardDescription>Session history (cleared on page refresh)</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={exportPasswords}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                  <Button
                    onClick={clearHistory}
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {passwordHistory.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-accent/50 transition-colors group"
                  >
                    <div className="flex-1 flex items-center gap-4">
                      <Badge variant="secondary" className="min-w-[100px] justify-center">
                        {entry.type}
                      </Badge>
                      <code className="font-mono text-sm flex-1">
                        {showPassword ? entry.password : '•'.repeat(entry.password.length)}
                      </code>
                      <span className="text-xs text-muted-foreground">{entry.timestamp}</span>
                    </div>
                    <Button
                      onClick={() => copyToClipboard(entry.password)}
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PasswordGeneratorPage;
